import { GET, POST } from 'utils/api';

import type { ActurisContactDetails } from 'api/quote-marketplace';

export const apiGetContactDetails = (
  contactRef: string,
): Promise<ActurisContactDetails> => {
  return GET(`api/acturis-customers/contacts/${contactRef}/details/`);
};

export interface ActurisContactPolicy {
  expiring_annual_premium?: string | null;
  contact_ref?: string;
  inception_date?: string | null;
  policy_ref: string;
  policy_year_ref?: string;
  version_ref?: string;
  part_ref?: string;
  policy_number?: string;
  carrier_name?: string;
  product_target_detail?: string;
  latest_policy_status?: string;
  latest_policy_status_detail?: string;
  business_event?: string;
  business_event_detail?: string;
  expiry_date?: string | null;
  cover_timezone?: string;
  product_target: string;
  product_name: string;
  policy_status: string;
  policy_status_detail: string | null;
  policyholder_name: string;
  effective_date: string | null;
}

export interface ActurisPolicyInformation {
  contact_ref: string;
  policy_ref: string;
  version_ref: string;
  policy_number: string;
  product_name: string;
  product_type: string;
  customer_type: string;
  business_event_detail: string;
  policy_status_detail: string;
  province: string;
  branch: string;
  producer: string;
  csr: string;
  carrier_name: string;
  insurer_account: string;
  effective_date: string | null;
  expiry_date: string | null;
  inception_date: string | null;
  term_length: string;
  commission_rate: string | null;
  payment_method: string;
  payment_plan: string;
  reporting_fields: { label: string; value: string }[];
}

export const apiGetContactPolicyInformation = (
  contactRef: string,
  policyRef: string,
  versionRef: string,
): Promise<ActurisPolicyInformation> =>
  GET(
    `api/acturis-customers/contacts/${encodeURIComponent(contactRef)}/policies/${encodeURIComponent(policyRef)}/versions/${encodeURIComponent(versionRef)}/`,
  );

// Number of policy-version records requested per page (backend maximum: 50).
export const CONTACT_POLICIES_PAGE_SIZE = 6;

export const apiGetContactPolicies = (
  contactRef: string,
  page = 1,
): Promise<ActurisContactPolicy[]> => {
  return GET(
    `api/acturis-customers/contacts/${contactRef}/policies/?page=${page}&page_size=${CONTACT_POLICIES_PAGE_SIZE}`,
  );
};

export const apiGetContactPolicyTypes = (
  contactRef: string,
): Promise<{ policy_types: string[] }> => {
  return GET(`api/acturis-customers/contacts/${contactRef}/policy-types/`);
};

export interface ActurisRelatedContact {
  contact_related_contact_ref: string;
  related_contact_ref: string;
  name: string;
  relationship: string;
  relationship_detail: string;
  interested_party_type: string;
}

export const apiGetContactRelatedContacts = (
  contactRef: string,
): Promise<ActurisRelatedContact[]> => {
  return GET(`api/acturis-customers/contacts/${contactRef}/related-contacts/`);
};

export interface ActurisRelatedContactOption {
  value: string;
  label: string;
}

export interface ActurisInterestedPartyOption extends ActurisRelatedContactOption {
  relationship: string;
}

export interface ActurisRelatedContactOptions {
  relationship_types: ActurisRelatedContactOption[];
  interested_party_types: ActurisInterestedPartyOption[];
}

export const apiGetContactRelatedContactOptions = (
  contactRef: string,
): Promise<ActurisRelatedContactOptions> => {
  return GET(
    `api/acturis-customers/contacts/${contactRef}/related-contacts/options/`,
  );
};

export interface ActurisAddRelatedContactPayload {
  related_contact_ref: string;
  relationship: string;
  interested_party_type?: string;
}

export const apiAddContactRelatedContact = (
  contactRef: string,
  payload: ActurisAddRelatedContactPayload,
): Promise<ActurisRelatedContact[]> => {
  return POST(
    `api/acturis-customers/contacts/${contactRef}/related-contacts/`,
    payload,
  );
};

export interface ActurisAgedDebt {
  not_yet_due: string | null;
  days_1_to_30: string | null;
  days_31_to_60: string | null;
  days_61_to_90: string | null;
  days_91_plus: string | null;
}

export interface ActurisAccountSummary {
  account_balance: string | null;
  outstanding_amount: string | null;
  unallocated_cash: string | null;
  aged_debt: ActurisAgedDebt;
}

export const apiGetContactAccountSummary = (
  contactRef: string,
): Promise<ActurisAccountSummary> => {
  return GET(`api/acturis-customers/contacts/${contactRef}/account-summary/`);
};
